import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
    TableLayoutType,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE } from "@/utils/wordGenerator/common.js";

export const createFPTemp2 = async (proposal) => {
    const style = proposal.style || {};
    const colorScheme = style.colorScheme || {};
    const headerColor = fixColor(colorScheme.header || style.box?.color || "#f0f0f0");
    const bodyColor = fixColor(colorScheme.body || style.box?.color || "#f0f0f0");
    const footerColor = fixColor(colorScheme.footer || style.box?.color || "#f0f0f0");

    const LOGO_WIDTH = 3200;
    const GAP = 400;
    const HALF_WIDTH_BOX = 5753;

    const HEADER_HEIGHT = 1200;
    const SPACER_HEIGHT = 650;
    const FOOTER_HEIGHT = 1200;
    const INFO_HEIGHT = 4950;
    const PROJECTS_HEIGHT = 5338;

    let logoRun = null;
    if (proposal.logo) {
        const logoData = await fetchImageWithDimensions(proposal.logo);
        if (logoData) {
            logoRun = fitImage(logoData, LOGO_WIDTH, HEADER_HEIGHT, "contain");
        }
    }

    // Prepare project images
    const projectRuns = [];
    for (const project of (proposal.projects || []).slice(0, 2)) {
        const photo = project.uploadedPhoto || project.photoURL;
        if (photo) {
            try {
                const imgData = await fetchImageWithDimensions(photo);
                projectRuns.push(fitImage(imgData, HALF_WIDTH_BOX, PROJECTS_HEIGHT, "cover"));
            } catch (e) {
                console.error("Project image failed:", e);
                projectRuns.push(null);
            }
        } else {
            projectRuns.push(null);
        }
    }

    // Placeholder children if image is missing to ensure height is maintained
    const getPlaceholder = () => [
        new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
            },
            rows: [
                new TableRow({
                    height: { value: PROJECTS_HEIGHT, rule: HeightRule.EXACT },
                    children: [new TableCell({ shading: { fill: bodyColor }, children: [] })]
                })
            ]
        })
    ];

    // 1. Header Table
    const headerTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
        },
        rows: [
            new TableRow({
                height: { value: HEADER_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: 27, type: WidthType.PERCENTAGE },
                        shading: { fill: headerColor },
                        verticalAlign: VerticalAlign.CENTER,
                        children: logoRun
                            ? [new Paragraph({ alignment: AlignmentType.CENTER, children: [logoRun] })]
                            : [new Paragraph("")]
                    }),
                    new TableCell({ width: { size: 3, type: WidthType.PERCENTAGE }, shading: { fill: "FFFFFF" }, children: [] }),
                    new TableCell({
                        width: { size: 70, type: WidthType.PERCENTAGE },
                        shading: { fill: headerColor },
                        children: []
                    })
                ]
            })
        ]
    });

    // 2. Info Table
    const infoTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
        },
        rows: [
            new TableRow({
                height: { value: INFO_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        columnSpan: 5,
                        shading: { fill: bodyColor },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: {
                            top: 1000,
                            bottom: 1500,
                            left: 700,
                            right: 700
                        },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                    new TextRun({
                                        text: proposal.name || "N/A",
                                        color: fixColor(style.heading?.color),
                                        size: (style.heading?.size || 24) * SCALE,
                                        font: style.heading?.font,
                                        bold: style.heading?.bold,
                                        italics: style.heading?.italic,
                                        underline: style.heading?.underline,
                                    })
                                ]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                border: {
                                    bottom: {
                                        color: fixColor(style.heading?.color),
                                        space: 1,
                                        style: BorderStyle.SINGLE,
                                        size: 20,
                                    }
                                },
                                spacing: {
                                    after: 1000
                                },
                                children: [
                                    new TextRun({
                                        text: `${proposal.address || 'N/A'}, ${proposal.city || 'N/A'}, ${proposal.stateProvince || 'N/A'}`,
                                        color: fixColor(style.heading?.color),
                                        size: (style.heading?.size || 18) * SCALE,
                                        font: style.heading?.font,
                                        bold: style.heading?.bold,
                                        italics: style.heading?.italic,
                                        underline: style.heading?.underline,
                                    })
                                ]
                            }),
                            // Nested Table for 50/50 submission info
                            new Table({
                                width: { size: 100, type: WidthType.PERCENTAGE },
                                borders: { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE }, insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE } },
                                rows: [
                                    new TableRow({
                                        children: [
                                            new TableCell({
                                                children: [
                                                    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "SUBMISSION DATE", color: fixColor(style.label?.color), size: (style.label?.size || 12) * SCALE, font: style.label?.font, bold: style.label?.bold, italics: style.label?.italic, underline: style.label?.underline })] }),
                                                    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: proposal.submissionDate || "N/A", color: fixColor(style.text?.color), size: (style.text?.size || 12) * SCALE, font: style.text?.font, bold: style.text?.bold, italics: style.text?.italic, underline: style.text?.underline })] })
                                                ]
                                            }),
                                            new TableCell({
                                                children: [
                                                    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "SUBMITTED BY", color: fixColor(style.label?.color), size: (style.label?.size || 12) * SCALE, font: style.label?.font, bold: style.label?.bold, italics: style.label?.italic, underline: style.label?.underline })] }),
                                                    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: proposal.submittedBy || "N/A", color: fixColor(style.text?.color), size: (style.text?.size || 12) * SCALE, font: style.text?.font, bold: style.text?.bold, italics: style.text?.italic, underline: style.text?.underline })] })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });

    // 3. Projects Table
    const projectsTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
        },
        rows: [
            new TableRow({
                height: { value: PROJECTS_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        columnSpan: 3,
                        width: { size: HALF_WIDTH_BOX, type: WidthType.DXA },
                        children: projectRuns[0] ? [new Paragraph({ children: [projectRuns[0]] })] : getPlaceholder()
                    }),
                    new TableCell({ width: { size: GAP, type: WidthType.DXA }, children: [] }), // middle gap
                    new TableCell({
                        width: { size: HALF_WIDTH_BOX, type: WidthType.DXA },
                        children: projectRuns[1] ? [new Paragraph({ children: [projectRuns[1]] })] : getPlaceholder()
                    })
                ]
            })
        ]
    });

    // 4. Footer Table
    const footerTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
        },
        rows: [
            new TableRow({
                height: { value: FOOTER_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        columnSpan: 5,
                        shading: { fill: footerColor },
                        children: []
                    })
                ]
            })
        ]
    });

    return {
        properties: { page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } } },
        children: [
            headerTable,
            new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
            infoTable,
            new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
            projectsTable,
            new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
            footerTable
        ],
    };
};

